import { motion, useMotionTemplate, useMotionValue } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PORTFOLIO } from "@/constants/testIds";

const PROJECTS = [
  {
    slug: "gym-management",
    n: "01 / 03",
    year: "2024",
    title: "Iron Ledger",
    tagline: "Gym Management Software",
    body:
      "A full-stack platform for boutique fitness studios: memberships, class scheduling, POS, biometric check-ins, and coach dashboards — replacing four legacy tools with one.",
    tags: ["Web App", "Mobile", "Cloud"],
    image:
      "https://images.pexels.com/photos/4162581/pexels-photo-4162581.jpeg",
    align: "left",
  },
  {
    slug: "ai-assistant",
    n: "02 / 03",
    year: "2025",
    title: "F.R.I.D.A.Y",
    tagline: "Personal AI Assistant",
    body:
      "A voice-first personal AI that lives on desktop and mobile — remembers context, drafts emails, plans days, and calls tools with sub-second latency.",
    tags: ["AI", "Voice", "Mobile"],
    image:
      "https://images.unsplash.com/photo-1709120395858-92f1c7c577f5",
    align: "right",
  },
  {
    slug: "analytics-dashboard",
    n: "03 / 03",
    year: "2023",
    title: "Data Analytics",
    tagline: "Flexible Dashboard for Analysis",
    body:
      "A drag-and-drop analytics canvas for ops teams: connect any warehouse, compose custom charts, and share live dashboards without a data engineer.",
    tags: ["SaaS", "Data", "Web"],
    image:
      "https://images.unsplash.com/photo-1687125114664-39c108bdbffd",
    align: "left",
  },
];

const SpotlightCard = ({ p, index }) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const bg = useMotionTemplate`radial-gradient(500px circle at ${mouseX}px ${mouseY}px, rgba(0,208,132,0.15), transparent 65%)`;

  const isRight = p.align === "right";

  return (
    <motion.article
      data-testid={PORTFOLIO.card(p.slug)}
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        mouseX.set(e.clientX - rect.left);
        mouseY.set(e.clientY - rect.top);
      }}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 1, ease: [0.22, 0.61, 0.36, 1] }}
      className="group relative overflow-hidden rounded-[28px] border border-white/[0.07] bg-[#09120E]/85 p-6 md:p-10"
    >
      <motion.div
        aria-hidden
        style={{ background: bg }}
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
      />

      <div className={`relative grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center ${isRight ? "md:[direction:rtl]" : ""}`}>
        {/* Image (clipped frame with spotlight glow) */}
        <div className="md:col-span-7 [direction:ltr]">
          <div className="relative overflow-hidden rounded-2xl aspect-[16/11] bg-[#050A08]">
            <div
              aria-hidden
              className="absolute -inset-8 opacity-70 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background:
                  "radial-gradient(closest-side at 30% 30%, rgba(0,208,132,0.35), transparent 65%)",
                filter: "blur(30px)",
              }}
            />
            <img
              src={p.image}
              alt={p.tagline}
              loading="lazy"
              className="relative w-full h-full object-cover transform-gpu will-change-transform transition-transform duration-[900ms] ease-out group-hover:scale-[1.045]"
            />
            {/* Frame overlay */}
            <div className="absolute inset-0 ring-1 ring-inset ring-white/[0.08] rounded-2xl" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050A08]/60 via-transparent to-transparent" />
          </div>
        </div>

        {/* Copy */}
        <div className="md:col-span-5 [direction:ltr]">
          <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.24em] text-white/40 mb-6">
            <span>{p.n}</span>
            <span>{p.year}</span>
          </div>
          <div className="text-emerald-300/90 uppercase tracking-[0.24em] text-[11px] mb-4">
            {p.tagline}
          </div>
          <h3 className="font-display font-bold text-white text-4xl md:text-5xl tracking-tight leading-[1.02] mb-5">
            {p.title}
          </h3>
          <p className="text-white/65 text-base md:text-[17px] leading-relaxed mb-7 max-w-[46ch]">
            {p.body}
          </p>
          <div className="flex flex-wrap gap-2 mb-8">
            {p.tags.map((t) => (
              <span
                key={t}
                className="text-[11px] uppercase tracking-[0.18em] rounded-full border border-white/10 px-3 py-1.5 text-white/70"
              >
                {t}
              </span>
            ))}
          </div>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-white/85 hover:text-emerald-300 link-underline text-sm"
          >
            Request a walkthrough
            <ArrowUpRight size={16} />
          </a>
        </div>
      </div>
    </motion.article>
  );
};

export const Portfolio = () => {
  return (
    <section
      id="work"
      data-testid={PORTFOLIO.section}
      className="relative py-28 md:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20"
        >
          <div>
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-emerald-300/80 mb-5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
              Selected work
            </div>
            <h2 className="font-display font-bold text-white text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.98] max-w-2xl">
              Products we&apos;re <br />
              <span className="italic font-normal text-emerald-300">quietly proud of.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-base md:text-lg">
            A small sample of the software we&apos;ve shipped - enough to show how we think, and how we build.
          </p>
        </motion.div>

        <div className="space-y-10 md:space-y-14">
          {PROJECTS.map((p, i) => (
            <SpotlightCard p={p} index={i} key={p.slug} />
          ))}
        </div>
      </div>
    </section>
  );
};
